import React, { useEffect, useState } from 'react';
import { apiCall } from '../utils/api';

interface BandMember {
  id: number;
  username: string;
  display_name: string;
  instrument: string;
  is_admin: boolean;
  is_active: boolean;
  email: string | null;
  phone: string | null;
  notes: string | null;
  default_page: string | null;
  created_at: string;
}

interface Session {
  id: number;
  member_id: number;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  last_active_at: string;
  expires_at: string;
  username: string;
  display_name: string;
  is_admin: boolean;
}

const INSTRUMENTS = [
  { value: 'bass', label: 'Bass' },
  { value: 'guitar1', label: 'Guitar 1' },
  { value: 'guitar2', label: 'Guitar 2' },
  { value: 'keys', label: 'Keys' },
  { value: 'drums', label: 'Drums' },
  { value: 'fiddle', label: 'Fiddle / Mandolin' },
  { value: 'vocals', label: 'Vocals' },
  { value: 'other', label: 'Other' },
];

// Landing pages a member can be sent to after logging in. Keep in step with
// VALID_DEFAULT_PAGES in server/src/routes/auth.ts - the server rejects anything else.
const DEFAULT_PAGES = [
  { value: '', label: 'Automatic (admins: Queue, others: Dashboard)' },
  { value: '/gig/queue', label: 'Queue / Admin' },
  { value: '/gig/band', label: 'Band Chart View' },
  { value: '/gig/lyrics', label: 'Current Song Lyrics' },
];

const BandMemberAdminPage: React.FC = () => {
  const [members, setMembers] = useState<BandMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState<BandMember | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    displayName: '',
    instrument: 'vocals',
    isAdmin: false,
    email: '',
    phone: '',
    notes: '',
    defaultPage: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Sessions state
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);

  const fetchSessions = async () => {
    try {
      const res = await apiCall('/api/auth/sessions');
      if (!res.ok) throw new Error('Failed to fetch sessions');
      const data = await res.json();
      setSessions(data.sessions);
    } catch (err: any) {
      console.error('Failed to fetch sessions:', err);
    } finally {
      setSessionsLoading(false);
    }
  };

  const bootSession = async (sessionId: number) => {
    try {
      const res = await apiCall(`/api/auth/sessions/${sessionId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to revoke session');
      await fetchSessions();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const bootAllForMember = async (memberId: number, displayName: string) => {
    if (!window.confirm(`Boot all sessions for ${displayName}?`)) return;
    try {
      const res = await apiCall(`/api/auth/sessions/member/${memberId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to revoke sessions');
      await fetchSessions();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const fetchMembers = async () => {
    try {
      const res = await apiCall('/api/auth/members');
      if (!res.ok) throw new Error('Failed to fetch members');
      const data = await res.json();
      setMembers(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
    fetchSessions();
  }, []);

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      displayName: '',
      instrument: 'vocals',
      isAdmin: false,
      email: '',
      phone: '',
      notes: '',
      defaultPage: '',
    });
    setEditingMember(null);
    setFormError(null);
  };

  const handleEdit = (member: BandMember) => {
    setEditingMember(member);
    setFormData({
      username: member.username,
      password: '',
      displayName: member.display_name,
      instrument: member.instrument,
      isAdmin: member.is_admin,
      email: member.email || '',
      phone: member.phone || '',
      notes: member.notes || '',
      defaultPage: member.default_page || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSaving(true);

    try {
      if (editingMember) {
        // Update existing member
        const updateData: any = {
          displayName: formData.displayName,
          instrument: formData.instrument,
          isAdmin: formData.isAdmin,
          email: formData.email || null,
          phone: formData.phone || null,
          notes: formData.notes || null,
          defaultPage: formData.defaultPage,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }

        const res = await apiCall(`/api/auth/members/${editingMember.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updateData),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to update member');
        }
      } else {
        // Create new member
        if (!formData.password) {
          throw new Error('Password is required for new members');
        }

        const res = await apiCall('/api/auth/members', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to create member');
        }
      }

      await fetchMembers();
      setShowForm(false);
      resetForm();
    } catch (err: any) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (member: BandMember) => {
    if (!window.confirm(`Are you sure you want to delete ${member.display_name}?`)) {
      return;
    }

    try {
      const res = await apiCall(`/api/auth/members/${member.id}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to delete member');
      }

      await fetchMembers();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleToggleActive = async (member: BandMember) => {
    try {
      const res = await apiCall(`/api/auth/members/${member.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !member.is_active }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update member');
      }

      await fetchMembers();
    } catch (err: any) {
      alert(err.message);
    }
  };

  if (loading) {
    return <div style={{ padding: 24, color: '#f5deb3' }}>Loading...</div>;
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: 10,
    borderRadius: 4,
    border: '1px solid #5a3a1a',
    background: '#1a0f0a',
    color: '#f5deb3',
    fontSize: 14,
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: 4,
    fontWeight: 600,
    color: '#f5deb3',
    fontSize: 14,
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, color: '#ffd700', fontWeight: 700, letterSpacing: 1 }}>Band Member Management</h1>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          style={{
            padding: '10px 20px',
            background: 'linear-gradient(180deg, #d4a017, #b8860b)',
            color: '#1a0f0a',
            border: '1px solid #ffd700',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 16,
            fontWeight: 700,
          }}
        >
          + Add Member
        </button>
      </div>

      {error && <p style={{ color: '#e74c3c' }}>{error}</p>}

      {/* Member Form Modal */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: '#28190f',
            borderRadius: 12,
            padding: 24,
            width: '100%',
            maxWidth: 500,
            maxHeight: '90vh',
            overflow: 'auto',
            border: '1px solid #5a3a1a',
            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
          }}>
            <h2 style={{ marginTop: 0, color: '#ffd700' }}>
              {editingMember ? 'Edit Member' : 'Add New Member'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={e => setFormData({ ...formData, username: e.target.value })}
                  disabled={!!editingMember}
                  required
                  style={{
                    ...inputStyle,
                    opacity: editingMember ? 0.5 : 1,
                  }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>
                  Password {editingMember && '(leave blank to keep current)'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  required={!editingMember}
                  minLength={4}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Display Name</label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={e => setFormData({ ...formData, displayName: e.target.value })}
                  required
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Instrument</label>
                <select
                  value={formData.instrument}
                  onChange={e => setFormData({ ...formData, instrument: e.target.value })}
                  style={inputStyle}
                >
                  {INSTRUMENTS.map(inst => (
                    <option key={inst.value} value={inst.value}>{inst.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Landing Page</label>
                <select
                  value={formData.defaultPage}
                  onChange={e => setFormData({ ...formData, defaultPage: e.target.value })}
                  style={inputStyle}
                >
                  {DEFAULT_PAGES.map(pg => (
                    <option key={pg.value} value={pg.value}>{pg.label}</option>
                  ))}
                </select>
                <div style={{ color: '#a08050', fontSize: 12, marginTop: 4 }}>
                  Where this member lands after logging in.
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#f5deb3', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.isAdmin}
                    onChange={e => setFormData({ ...formData, isAdmin: e.target.checked })}
                    style={{
                      width: 18,
                      height: 18,
                      accentColor: '#ffd700',
                      cursor: 'pointer',
                    }}
                  />
                  <span>Admin privileges</span>
                </label>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  style={inputStyle}
                />
              </div>

              {formError && <p style={{ color: '#e74c3c' }}>{formError}</p>}

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); resetForm(); }}
                  style={{
                    padding: '10px 20px',
                    background: '#5a3a1a',
                    color: '#f5deb3',
                    border: '1px solid #5a3a1a',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    padding: '10px 20px',
                    background: editingMember
                      ? '#c8850f'
                      : 'linear-gradient(180deg, #d4a017, #b8860b)',
                    color: editingMember ? '#fff' : '#1a0f0a',
                    border: editingMember ? '1px solid #c8850f' : '1px solid #ffd700',
                    borderRadius: 6,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? 0.7 : 1,
                    fontWeight: 700,
                  }}
                >
                  {saving ? 'Saving...' : (editingMember ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Members Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: 8, overflow: 'hidden' }}>
        <thead>
          <tr style={{ background: '#3a1f0a' }}>
            <th style={{ padding: 12, textAlign: 'left', color: '#ffd700', borderBottom: '2px solid #5a3a1a' }}>Name</th>
            <th style={{ padding: 12, textAlign: 'left', color: '#ffd700', borderBottom: '2px solid #5a3a1a' }}>Username</th>
            <th style={{ padding: 12, textAlign: 'left', color: '#ffd700', borderBottom: '2px solid #5a3a1a' }}>Instrument</th>
            <th style={{ padding: 12, textAlign: 'center', color: '#ffd700', borderBottom: '2px solid #5a3a1a' }}>Status</th>
            <th style={{ padding: 12, textAlign: 'center', color: '#ffd700', borderBottom: '2px solid #5a3a1a' }}>Admin</th>
            <th style={{ padding: 12, textAlign: 'center', color: '#ffd700', borderBottom: '2px solid #5a3a1a' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {members.length === 0 ? (
            <tr>
              <td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#a08060' }}>
                No band members yet. Click "Add Member" to create one.
              </td>
            </tr>
          ) : (
            members.map((member, idx) => (
              <tr key={member.id} style={{
                background: idx % 2 === 0 ? 'rgba(40, 25, 15, 0.6)' : 'rgba(40, 25, 15, 0.3)',
                borderBottom: '1px solid #5a3a1a',
              }}>
                <td style={{ padding: 12, color: '#f5deb3' }}>{member.display_name}</td>
                <td style={{ padding: 12, fontFamily: 'monospace', color: '#a08060' }}>{member.username}</td>
                <td style={{ padding: 12, color: '#f5deb3' }}>
                  {INSTRUMENTS.find(i => i.value === member.instrument)?.label || member.instrument}
                </td>
                <td style={{ padding: 12, textAlign: 'center' }}>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 600,
                    background: member.is_active ? 'rgba(46, 204, 113, 0.15)' : 'rgba(231, 76, 60, 0.15)',
                    color: member.is_active ? '#2ecc71' : '#e74c3c',
                    border: `1px solid ${member.is_active ? '#2ecc71' : '#e74c3c'}`,
                  }}>
                    {member.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ padding: 12, textAlign: 'center', color: '#ffd700', fontWeight: 700 }}>
                  {member.is_admin ? 'Yes' : ''}
                </td>
                <td style={{ padding: 12, textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                    <button
                      onClick={() => handleEdit(member)}
                      style={{
                        padding: '6px 12px',
                        background: '#c8850f',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleActive(member)}
                      style={{
                        padding: '6px 12px',
                        background: member.is_active ? '#a08060' : '#2ecc71',
                        color: member.is_active ? '#1a0f0a' : '#1a0f0a',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {member.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleDelete(member)}
                      style={{
                        padding: '6px 12px',
                        background: '#e74c3c',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Active Sessions Section */}
      <div style={{ marginTop: 48 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, color: '#ffd700', fontWeight: 700 }}>
            Active Sessions ({sessions.length})
          </h2>
          <button
            onClick={fetchSessions}
            style={{
              padding: '8px 16px',
              background: '#5a3a1a',
              color: '#f5deb3',
              border: '1px solid #5a3a1a',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Refresh
          </button>
        </div>

        {sessionsLoading ? (
          <p style={{ color: '#a08060' }}>Loading sessions...</p>
        ) : sessions.length === 0 ? (
          <p style={{ color: '#a08060' }}>No active sessions.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: 8, overflow: 'hidden' }}>
            <thead>
              <tr style={{ background: '#3a1f0a' }}>
                <th style={{ padding: 12, textAlign: 'left', color: '#ffd700', borderBottom: '2px solid #5a3a1a' }}>User</th>
                <th style={{ padding: 12, textAlign: 'left', color: '#ffd700', borderBottom: '2px solid #5a3a1a' }}>IP</th>
                <th style={{ padding: 12, textAlign: 'left', color: '#ffd700', borderBottom: '2px solid #5a3a1a' }}>Last Active</th>
                <th style={{ padding: 12, textAlign: 'left', color: '#ffd700', borderBottom: '2px solid #5a3a1a' }}>Created</th>
                <th style={{ padding: 12, textAlign: 'center', color: '#ffd700', borderBottom: '2px solid #5a3a1a' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session, idx) => {
                const lastActive = new Date(session.last_active_at);
                const created = new Date(session.created_at);
                const minutesAgo = Math.floor((Date.now() - lastActive.getTime()) / 60000);
                const activeLabel = minutesAgo < 1 ? 'Just now' :
                  minutesAgo < 60 ? `${minutesAgo}m ago` :
                  minutesAgo < 1440 ? `${Math.floor(minutesAgo / 60)}h ago` :
                  `${Math.floor(minutesAgo / 1440)}d ago`;

                return (
                  <tr key={session.id} style={{
                    background: idx % 2 === 0 ? 'rgba(40, 25, 15, 0.6)' : 'rgba(40, 25, 15, 0.3)',
                    borderBottom: '1px solid #5a3a1a',
                  }}>
                    <td style={{ padding: 12, color: '#f5deb3' }}>
                      {session.display_name}
                      {session.is_admin && (
                        <span style={{ marginLeft: 8, fontSize: 11, color: '#ffd700', fontWeight: 600 }}>ADMIN</span>
                      )}
                    </td>
                    <td style={{ padding: 12, fontFamily: 'monospace', color: '#a08060', fontSize: 13 }}>
                      {session.ip_address || '-'}
                    </td>
                    <td style={{ padding: 12, color: minutesAgo < 5 ? '#2ecc71' : '#a08060', fontSize: 13 }}>
                      {activeLabel}
                    </td>
                    <td style={{ padding: 12, color: '#a08060', fontSize: 13 }}>
                      {created.toLocaleDateString()} {created.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={{ padding: 12, textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                        <button
                          onClick={() => bootSession(session.id)}
                          style={{
                            padding: '6px 12px',
                            background: '#e74c3c',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          Boot
                        </button>
                        <button
                          onClick={() => bootAllForMember(session.member_id, session.display_name)}
                          style={{
                            padding: '6px 12px',
                            background: '#a93226',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          Boot All
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BandMemberAdminPage;
